import { useId, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MarkdownEditor, type MarkdownEditorHandle } from '../ui/markdown-editor';
import { FIELD_LABEL_CLASS } from '../ui/section-header';

/** What the GM typed into the form of a new note. */
export interface NoteDraft {
  readonly name: string;
  /** «Видят игроки». */
  readonly content: string;
  /** «Для мастера». */
  readonly gmNotes: string;
}

export interface InlineNoteFormProps {
  readonly t: (key: keyof TranslationKeys) => string;
  /** «Готово»: only now the note is made (no «Новая заметка» is left behind by a cancelled form). */
  readonly onSubmit: (draft: NoteDraft) => void;
  readonly onCancel: () => void;
}

/**
 * GK-1: a new note is typed right in the card — the name (focus is already there), what the
 * players see, what is for the GM — and made by «Готово». Enter in the name moves on to the text;
 * Ctrl+Enter in a text is «Готово»; Escape cancels. The beat stays on the screen.
 */
export function InlineNoteForm({ t, onSubmit, onCancel }: InlineNoteFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [gmNotes, setGmNotes] = useState('');
  const playersRef = useRef<MarkdownEditorHandle>(null);
  const playersId = useId();
  const gmId = useId();
  const ready = name.trim() !== '';
  const submit = (): void => {
    if (!ready) return;
    onSubmit({ name: name.trim(), content, gmNotes });
  };
  return (
    <form
      className="flex flex-col gap-1.5 rounded-md border border-line bg-panel-2 px-2.5 py-2"
      data-testid="note-form"
      // What is typed here lives nowhere else until «Готово»: Tia's navigation does not replace the pane (P6).
      data-studio-unsaved={name.trim() !== '' || content.trim() !== '' || gmNotes.trim() !== '' ? '' : undefined}
      onSubmit={(e): void => {
        e.preventDefault();
        submit();
      }}
      onKeyDown={(e): void => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <Input
        autoFocus
        data-testid="note-form-name"
        aria-label={t('studio.noteForm.name')}
        placeholder={t('studio.noteForm.name')}
        value={name}
        onChange={(e): void => setName(e.target.value)}
        onKeyDown={(e): void => {
          if (e.key === 'Enter') {
            e.preventDefault();
            playersRef.current?.focus();
          }
        }}
      />
      {/* Not a <label>: a click on its words would press the first button of the editor's toolbar. */}
      <div className="flex flex-col gap-0.5">
        <span id={playersId} className={FIELD_LABEL_CLASS}>{t('studio.noteForm.players')}</span>
        <MarkdownEditor
          ref={playersRef}
          aria-labelledby={playersId}
          data-testid="note-form-players"
          rows={2}
          value={content}
          onValueChange={setContent}
          onKeyDown={(e): void => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>
      <div className="flex flex-col gap-0.5">
        <span id={gmId} className={FIELD_LABEL_CLASS}>{t('studio.noteForm.gm')}</span>
        <MarkdownEditor
          aria-labelledby={gmId}
          data-testid="note-form-gm"
          rows={2}
          value={gmNotes}
          onValueChange={setGmNotes}
          onKeyDown={(e): void => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <Button type="submit" size="sm" data-testid="note-form-done" disabled={!ready}>
          {t('studio.noteForm.done')}
        </Button>
        <Button type="button" size="sm" variant="outline" data-testid="note-form-cancel" onClick={onCancel}>
          {t('studio.noteForm.cancel')}
        </Button>
      </div>
    </form>
  );
}
